/*
  Sweet Baby — theme JS
  Three modules:
    - wishlist        localStorage-backed product favoriting
    - predictiveSearch  /search/suggest.json results in the header search dialog
    - quickAdd        AJAX add-to-cart on product cards + PDP, then opens cart drawer
*/

(function () {
  'use strict';

  // ───────────────────────────────────────────────────────────────────
  // Shared helpers

  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  // Re-trigger the cart-badge pop animation (CSS keyframe sb-badge-bump).
  const bumpBadge = (el) => {
    if (!el) return;
    el.classList.remove('is-bumped');
    void el.offsetWidth; // force reflow so the animation restarts
    el.classList.add('is-bumped');
  };

  // Mirrors snippets/dual-price.liquid: "X,YY € / Z,WW лв." (EUR primary,
  // BGN at the fixed 1.95583 peg, comma decimal). The .js endpoints return
  // prices in the store currency (EUR) as integer cents.
  const formatMoney = (cents) => {
    const fmt = (n) => n.toFixed(2).replace('.', ',');
    return fmt(cents / 100) + ' € / ' + fmt((cents * 1.95583) / 100) + ' лв.';
  };

  const refreshCartUI = async () => {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());

      // header count badge — pop it when the count grows (added item feedback)
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        if (cart.item_count > 0) {
          const grew = cart.item_count > (parseInt(el.textContent, 10) || 0);
          el.textContent = cart.item_count;
          el.style.display = '';
          if (grew) bumpBadge(el);
        } else {
          el.style.display = 'none';
        }
      });

      // re-render the cart drawer body from a fresh fetch of the current page
      const drawer = document.getElementById('cart-drawer');
      if (drawer) {
        const html = await fetch(window.location.pathname + '?section_id=header').then((r) => r.text());
        const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('cart-drawer');
        if (fresh) drawer.innerHTML = fresh.innerHTML;
      }
    } catch (err) {
      console.error('refreshCartUI', err);
    }
  };

  // ───────────────────────────────────────────────────────────────────
  // Wishlist (localStorage)

  const WISHLIST_KEY = 'sweetbaby:wishlist';

  const getWishlist = () => {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch {
      return [];
    }
  };

  const setWishlist = (list) => {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    syncWishlistUI();
  };

  const toggleWishlist = (handle) => {
    const list = getWishlist();
    const i = list.indexOf(handle);
    if (i >= 0) list.splice(i, 1);
    else list.push(handle);
    setWishlist(list);
  };

  const syncWishlistUI = () => {
    const list = getWishlist();
    // mark toggle buttons (filled state via aria-pressed)
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const handle = btn.dataset.productHandle;
      const on = list.includes(handle);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('text-rose-taupe-700', on);
      const svg = btn.querySelector('svg');
      if (svg) svg.setAttribute('fill', on ? 'currentColor' : 'none');
    });
    // header badge
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      if (list.length > 0) {
        el.textContent = list.length;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  };

  const renderWishlistDrawer = async () => {
    const drawer = document.getElementById('wishlist-drawer');
    const body = drawer?.querySelector('[data-wishlist-drawer-body]');
    if (!body) return;

    const list = getWishlist();
    if (list.length === 0) {
      body.innerHTML =
        '<div class="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">' +
        '<p class="text-base font-light text-rose-taupe-500">Списъкът ти с любими е празен.</p>' +
        '<a href="/collections/all" class="mt-2 inline-flex items-center rounded-full bg-rose-taupe-500 px-6 py-2.5 text-sm font-medium text-white hover:bg-rose-taupe-600">Разгледай играчките</a>' +
        '</div>';
      return;
    }

    body.innerHTML = '<p class="p-6 text-sm text-rose-taupe-500">Зарежда се…</p>';

    const products = await Promise.all(
      list.map((h) =>
        fetch('/products/' + encodeURIComponent(h) + '.js')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    body.innerHTML =
      '<ul class="flex-1 space-y-5 overflow-y-auto p-6">' +
      products
        .map((p, idx) => {
          if (!p) {
            return (
              '<li class="flex items-center justify-between gap-3 text-sm text-rose-taupe-500">' +
              '<span>Продуктът вече не е наличен</span>' +
              '<button type="button" data-wishlist-remove="' +
              list[idx] +
              '" class="rounded-full px-3 py-1 text-xs font-medium text-rose-taupe-700 hover:bg-rose-taupe-100">Премахни</button>' +
              '</li>'
            );
          }
          const img = p.featured_image
            ? '<img src="' +
              p.featured_image +
              '" alt="' +
              (p.title || '').replace(/"/g, '&quot;') +
              '" class="h-full w-full object-cover">'
            : '';
          return (
            '<li class="flex gap-4">' +
            '<a href="' +
            p.url +
            '" class="block size-20 shrink-0 overflow-hidden rounded-2xl bg-rose-taupe-100">' +
            img +
            '</a>' +
            '<div class="min-w-0 flex-1 space-y-1">' +
            '<a href="' +
            p.url +
            '" class="block font-light leading-6 text-rose-taupe-700 hover:underline">' +
            p.title +
            '</a>' +
            '<p class="text-sm font-semibold text-rose-taupe-700">' +
            formatMoney(p.price) +
            '</p>' +
            '<button type="button" data-wishlist-remove="' +
            p.handle +
            '" class="text-xs text-rose-taupe-500 underline-offset-2 hover:underline">Премахни</button>' +
            '</div>' +
            '</li>'
          );
        })
        .join('') +
      '</ul>';
  };

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      e.preventDefault();
      const handle = toggle.dataset.productHandle;
      if (handle) toggleWishlist(handle);
      return;
    }
    const trigger = e.target.closest('[data-wishlist-trigger]');
    if (trigger) {
      e.preventDefault();
      const drawer = document.getElementById('wishlist-drawer');
      if (drawer) {
        renderWishlistDrawer();
        drawer.showModal();
      }
      return;
    }
    const remove = e.target.closest('[data-wishlist-remove]');
    if (remove) {
      e.preventDefault();
      toggleWishlist(remove.dataset.wishlistRemove);
      renderWishlistDrawer();
      return;
    }
  });

  // ───────────────────────────────────────────────────────────────────
  // Predictive search

  const wireSearch = () => {
    const input = document.getElementById('search-input');
    const results = document.getElementById('search-results');
    if (!input || !results) return;

    const search = debounce(async (q) => {
      if (!q || q.length < 2) {
        results.innerHTML = '';
        results.classList.add('hidden');
        return;
      }
      try {
        const url =
          '/search/suggest.json?q=' +
          encodeURIComponent(q) +
          '&resources[type]=product,collection,article&resources[limit]=4';
        const data = await fetch(url).then((r) => r.json());
        const r = data?.resources?.results || {};
        const items = [];
        (r.products || []).forEach((p) => {
          items.push(
            '<a href="' +
              p.url +
              '" class="flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-rose-taupe-50">' +
              (p.image
                ? '<img src="' + p.image + '" alt="" class="size-12 shrink-0 rounded-lg object-cover">'
                : '<span class="size-12 shrink-0 rounded-lg bg-rose-taupe-100"></span>') +
              '<span class="flex-1"><span class="block text-sm text-rose-taupe-700">' +
              p.title +
              '</span>' +
              (p.price
                ? '<span class="block text-xs text-rose-taupe-500">' + p.price + '</span>'
                : '') +
              '</span></a>'
          );
        });
        (r.collections || []).forEach((c) => {
          items.push(
            '<a href="' +
              c.url +
              '" class="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-rose-taupe-700 hover:bg-rose-taupe-50">' +
              '<span class="text-xs uppercase tracking-wide text-rose-taupe-400">Колекция</span>' +
              '<span>' +
              c.title +
              '</span></a>'
          );
        });
        (r.articles || []).forEach((a) => {
          items.push(
            '<a href="' +
              a.url +
              '" class="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-rose-taupe-700 hover:bg-rose-taupe-50">' +
              '<span class="text-xs uppercase tracking-wide text-rose-taupe-400">Статия</span>' +
              '<span>' +
              a.title +
              '</span></a>'
          );
        });
        results.innerHTML = items.length
          ? '<div class="space-y-1 p-3">' + items.join('') + '</div>'
          : '<p class="p-6 text-sm text-rose-taupe-500">Няма резултати за „' + q + '"</p>';
        results.classList.remove('hidden');
      } catch (err) {
        console.error('predictive search', err);
      }
    }, 200);

    input.addEventListener('input', (e) => search(e.target.value.trim()));
  };

  // ───────────────────────────────────────────────────────────────────
  // Header expanding search: icon click expands the field leftward; click-away
  // / Esc closes; clicking the icon while open submits (or closes if empty).

  const wireHeaderSearch = () => {
    const wrap = document.querySelector('[data-header-search]');
    if (!wrap) return;
    const header = wrap.closest('header');
    const toggle = wrap.querySelector('[data-header-search-toggle]');
    const input = wrap.querySelector('#search-input');
    const results = wrap.querySelector('#search-results');
    const spacer = header && header.querySelector('[data-cluster-spacer]');
    if (!header) return;

    const isOpen = () => header.hasAttribute('data-search-open');

    // Size the in-flow spacer to the floating cluster's closed width so the
    // header layout is identical whether the search is open or closed.
    const sizeSpacer = () => {
      if (spacer && !isOpen()) spacer.style.width = wrap.getBoundingClientRect().width + 'px';
    };
    sizeSpacer();
    window.addEventListener('resize', sizeSpacer);
    const close = () => {
      header.removeAttribute('data-search-open');
      if (results) results.classList.add('hidden');
    };
    const open = () => {
      header.setAttribute('data-search-open', '');
      setTimeout(() => input && input.focus(), 80);
    };
    const submit = () => {
      const q = input.value.trim();
      if (q) window.location.href = '/search?type=product&q=' + encodeURIComponent(q);
    };

    toggle.addEventListener('click', () => {
      if (isOpen()) {
        if (input.value.trim()) submit();
        else close();
      } else {
        open();
      }
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });

    document.addEventListener('click', (e) => {
      if (isOpen() && !wrap.contains(e.target)) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // AJAX quick-add (product cards + PDP)

  // Briefly morph the add-to-cart button into a success (checkmark) state so
  // the action feels confirmed before the cart drawer slides in.
  const CHECK_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="size-6" aria-hidden="true"><path d="m20 6-11 11-5-5"/></svg>';

  const showBtnSuccess = (btn) => {
    if (!btn || btn.dataset.added) return;
    btn.dataset.added = '1';
    btn.dataset.originalHtml = btn.innerHTML;
    const hadText = btn.textContent.trim().length > 0;
    btn.innerHTML = hadText ? CHECK_SVG + '<span>Добавено</span>' : CHECK_SVG;
    btn.classList.add('is-added');
  };

  const restoreBtn = (btn) => {
    if (!btn || !btn.dataset.added) return;
    btn.innerHTML = btn.dataset.originalHtml || btn.innerHTML;
    btn.classList.remove('is-added');
    delete btn.dataset.added;
    delete btn.dataset.originalHtml;
  };

  const wireQuickAdd = () => {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-quick-add-form], [data-add-to-cart-form]');
      if (!form) return;
      e.preventDefault();

      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      try {
        const fd = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'add to cart failed');
        }
        await refreshCartUI();

        // Success state on the button, then open the drawer a beat later so the
        // checkmark is perceptible; restore the button once the drawer is open.
        if (submitBtn) showBtnSuccess(submitBtn);
        const drawer = document.getElementById('cart-drawer');
        setTimeout(() => {
          if (drawer && typeof drawer.showModal === 'function' && !drawer.open) drawer.showModal();
          if (submitBtn) {
            restoreBtn(submitBtn);
            submitBtn.disabled = false;
          }
        }, 650);
      } catch (err) {
        console.error('quick-add', err);
        alert('Грешка при добавяне в количката: ' + err.message);
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Hero carousel — auto-rotate, dot tracking, pause on hover/focus

  const wireHeroCarousel = () => {
    document.querySelectorAll('[data-hero-carousel]').forEach((root) => {
      const track = root.querySelector('[data-hero-track]');
      const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
      const dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
      if (!track || slides.length <= 1) return;

      const autorotate = root.dataset.autorotate === 'true';
      const interval = parseInt(root.dataset.autorotateInterval, 10) || 6000;
      let currentIdx = 0;
      let timer = null;
      let paused = false;

      const goTo = (idx) => {
        currentIdx = (idx + slides.length) % slides.length;
        const slide = slides[currentIdx];
        if (slide && track) {
          // Scroll only the carousel track horizontally — never the page.
          track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        }
      };

      const next = () => goTo(currentIdx + 1);

      const start = () => {
        if (!autorotate || paused) return;
        stop();
        timer = setInterval(next, interval);
      };
      const stop = () => {
        if (timer) clearInterval(timer);
        timer = null;
      };

      // Dot click → jump
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          goTo(i);
          // Restart timer so a user-driven jump resets the clock
          start();
        });
      });

      // Track active slide via IntersectionObserver, update dots
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
              const idx = slides.indexOf(entry.target);
              if (idx !== -1) {
                currentIdx = idx;
                dots.forEach((d, i) => {
                  if (i === idx) d.setAttribute('aria-current', 'true');
                  else d.removeAttribute('aria-current');
                });
              }
            }
          });
        },
        { root: track, threshold: 0.5 }
      );
      slides.forEach((s) => io.observe(s));

      // Pause on hover / focus
      root.addEventListener('mouseenter', () => { paused = true; stop(); });
      root.addEventListener('mouseleave', () => { paused = false; start(); });
      root.addEventListener('focusin', () => { paused = true; stop(); });
      root.addEventListener('focusout', () => { paused = false; start(); });

      // Pause when tab not visible (battery + politeness)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop();
        else if (!paused) start();
      });

      // Respect prefers-reduced-motion
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      start();
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Testimonials carousel — arrows scroll one slide at a time

  const wireTestimonialsCarousel = () => {
    document.querySelectorAll('[data-testimonial-carousel]').forEach((root) => {
      const track = root.querySelector('[data-testimonial-track]');
      const slides = Array.from(root.querySelectorAll('[data-testimonial-slide]'));
      const prev = root.querySelector('[data-testimonial-prev]');
      const next = root.querySelector('[data-testimonial-next]');
      const controls = root.querySelector('[data-testimonial-controls]');
      if (!track || slides.length === 0) return;

      const scrollByOneSlide = (direction) => {
        const slideWidth = slides[0].getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 16;
        track.scrollBy({ left: direction * (slideWidth + gap), behavior: 'smooth' });
      };

      prev?.addEventListener('click', () => scrollByOneSlide(-1));
      next?.addEventListener('click', () => scrollByOneSlide(1));

      // Hide the arrows entirely when everything fits (nothing to scroll);
      // otherwise reflect scroll position via the disabled state.
      const updateButtons = () => {
        const scrollable = track.scrollWidth > track.clientWidth + 4;
        if (controls) controls.hidden = !scrollable;
        const atStart = track.scrollLeft <= 4;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        if (prev) prev.disabled = atStart;
        if (next) next.disabled = atEnd;
      };
      updateButtons();
      track.addEventListener('scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons);

      // Auto-rotate — advance every 5s, looping back to the start at the end.
      // Pauses on hover / touch and when the tab is hidden; respects reduced-motion.
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reduceMotion && slides.length > 1) {
        let timer = null;
        const advance = () => {
          const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
          if (atEnd) track.scrollTo({ left: 0, behavior: 'smooth' });
          else scrollByOneSlide(1);
        };
        const startAuto = () => { if (!timer) timer = setInterval(advance, 5000); };
        const stopAuto = () => { if (timer) { clearInterval(timer); timer = null; } };
        root.addEventListener('mouseenter', stopAuto);
        root.addEventListener('mouseleave', startAuto);
        root.addEventListener('pointerdown', stopAuto);
        root.addEventListener('pointerup', () => setTimeout(startAuto, 3000));
        document.addEventListener('visibilitychange', () => { document.hidden ? stopAuto() : startAuto(); });
        startAuto();
      }
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Blog carousel (mobile only — dots track + click-to-scroll)

  const wireBlogCarousel = () => {
    document.querySelectorAll('[data-blog-carousel]').forEach((root) => {
      const track = root.querySelector('ul');
      const dots = Array.from(root.querySelectorAll('[data-blog-carousel-dot]'));
      if (!track || dots.length === 0) return;
      const slides = Array.from(track.children);

      // Click dot → scroll to slide (track only, never the page)
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          const slide = slides[i];
          if (slide) {
            track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
          }
        });
      });

      // Active slide tracking via IntersectionObserver
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
              const idx = slides.indexOf(entry.target);
              dots.forEach((d, i) => {
                if (i === idx) d.setAttribute('aria-current', 'true');
                else d.removeAttribute('aria-current');
              });
            }
          });
        },
        { root: track, threshold: 0.5 }
      );
      slides.forEach((s) => io.observe(s));
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Recently-viewed products (populated from localStorage)

  const wireLastViewed = () => {
    const root = document.querySelector('[data-last-viewed]');
    if (!root) return;
    const list = root.querySelector('[data-last-viewed-list]');
    if (!list) return;

    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem('sweetbaby:recently-viewed')) || [];
    } catch {
      handles = [];
    }

    // Exclude the current product if we're on a PDP. Derive the handle from the
    // URL — the inline variants JSON has no handle field, so the old lookup
    // never matched and the current product showed up in its own list.
    const pdpMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (pdpMatch) {
      const currentHandle = decodeURIComponent(pdpMatch[1]);
      handles = handles.filter((h) => h !== currentHandle);
    }

    const max = parseInt(root.dataset.productsToShow, 10) || 4;
    handles = handles.slice(0, max);

    if (handles.length === 0) {
      root.hidden = true;
      return;
    }

    root.hidden = false;
    list.innerHTML = handles
      .map(
        () =>
          '<li class="aspect-[4/5] animate-pulse rounded-[32px] bg-rose-taupe-100"></li>'
      )
      .join('');

    Promise.all(
      handles.map((h) =>
        fetch('/products/' + encodeURIComponent(h) + '.js')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    ).then(async (products) => {
      const valid = products.filter(Boolean);
      if (valid.length === 0) {
        root.hidden = true;
        return;
      }
      // Render client-side cards that mirror snippets/product-card.liquid exactly
      // (same 225px landscape image window, hover-zoom, wishlist button, sale
      // badge/price and quick-add icon) so the recently-viewed row is visually
      // identical to every other product grid.
      const HEART_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="size-6" aria-hidden="true"><path d="M19.5 12.572 12 20l-7.5-7.428A5 5 0 1 1 12 6.005a5 5 0 1 1 7.5 6.567Z"/></svg>';
      const CART_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" width="21" height="22" viewBox="0 0 21 22" fill="none" class="size-[21px]" aria-hidden="true"><path d="M14.1625 8V5C14.1625 2.79086 12.3716 1 10.1625 1C7.95334 1 6.16248 2.79086 6.16248 5V8M1.75448 9.35196L1.15448 15.752C0.983885 17.5717 0.898586 18.4815 1.20053 19.1843C1.46578 19.8016 1.9306 20.3121 2.5205 20.6338C3.192 21 4.10585 21 5.93353 21H14.3914C16.2191 21 17.133 21 17.8045 20.6338C18.3944 20.3121 18.8592 19.8016 19.1244 19.1843C19.4264 18.4815 19.3411 17.5717 19.1705 15.752L18.5705 9.35197C18.4264 7.81535 18.3544 7.04704 18.0088 6.46616C17.7045 5.95458 17.2548 5.54511 16.7171 5.28984C16.1065 5 15.3348 5 13.7914 5L6.53353 5C4.99017 5 4.21849 5 3.6079 5.28984C3.07014 5.54511 2.62049 5.95458 2.31614 6.46616C1.97057 7.04704 1.89854 7.81534 1.75448 9.35196Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      list.innerHTML = valid
        .map((p) => {
          const title = (p.title || '').replace(/"/g, '&quot;');
          const cmp = p.compare_at_price_max || p.compare_at_price || 0;
          const onSale = cmp > p.price;
          const discount = onSale ? Math.round((1 - p.price / cmp) * 100) : 0;
          const variantId = (p.variants && p.variants[0] && p.variants[0].id) || '';

          const imageInner = p.featured_image
            ? '<img src="' + p.featured_image + '" alt="' + title +
              '" class="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-[1.06]">'
            : '<div class="flex h-full w-full items-center justify-center text-rose-taupe-300">' + HEART_SVG + '</div>';

          return (
            '<li><article class="group relative flex h-full flex-col gap-4 rounded-[32px] bg-rose-taupe-100 p-[18px]">' +
              '<a href="' + p.url + '" class="relative block h-[225px] overflow-hidden rounded-[14px] bg-nude-50" aria-label="' + title + '">' +
                imageInner +
              '</a>' +
              '<div class="absolute inset-x-[34px] top-[34px] flex items-start justify-between gap-2">' +
                '<div class="flex flex-col gap-1.5">' +
                  (onSale
                    ? '<span class="inline-flex items-center justify-center rounded-full bg-rose-taupe-500 px-4 py-2 text-sm font-medium text-rose-taupe-50">−' + discount + '%</span>'
                    : '') +
                '</div>' +
                '<button type="button" class="ml-auto flex size-12 items-center justify-center rounded-full bg-rose-taupe-50 text-rose-taupe-500 transition-colors hover:text-rose-taupe-700" aria-label="Add to wishlist" aria-pressed="false" data-wishlist-toggle data-product-handle="' + p.handle + '">' +
                  HEART_SVG +
                '</button>' +
              '</div>' +
              '<div class="flex flex-1 flex-col justify-between gap-4">' +
                '<h3 class="text-base font-light leading-6 text-rose-taupe-700"><a href="' + p.url + '" class="line-clamp-2 hover:text-rose-taupe-900">' + p.title + '</a></h3>' +
                '<div class="flex items-center justify-between gap-3">' +
                  '<div class="flex flex-col">' +
                    (onSale
                      ? '<span class="text-sm font-bold leading-snug text-rose-taupe-500 line-through opacity-50">' + formatMoney(cmp) + '</span>'
                      : '') +
                    '<span class="text-lg font-bold leading-snug text-rose-taupe-700">' + formatMoney(p.price) + '</span>' +
                  '</div>' +
                  '<form action="/cart/add" method="post" enctype="multipart/form-data" data-quick-add-form>' +
                    '<input type="hidden" name="id" value="' + variantId + '">' +
                    '<input type="hidden" name="quantity" value="1">' +
                    '<button type="submit" class="flex size-12 items-center justify-center rounded-full bg-rose-taupe-50 text-rose-taupe-400 transition-colors hover:bg-rose-taupe-500 hover:text-white disabled:opacity-60" aria-label="Add to cart">' +
                      CART_SVG +
                    '</button>' +
                  '</form>' +
                '</div>' +
              '</div>' +
            '</article></li>'
          );
        })
        .join('');

      // Sync the freshly-injected wishlist hearts with saved state.
      if (typeof syncWishlistUI === 'function') syncWishlistUI();
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Collection filters: force the <details> drawer open on desktop. The CSS
  // "display:block on closed-details children" hack no longer works in current
  // Chrome (closed <details> hides content via ::details-content), so on
  // desktop — where the mobile summary toggle is hidden — the filters would
  // otherwise be invisible. Mobile keeps the collapsible behaviour.

  const wireCollectionFilters = () => {
    const wrapper = document.querySelector('[data-collection-section]');
    if (!wrapper) return;
    const sectionId = wrapper.dataset.sectionId;
    const desktop = window.matchMedia('(min-width: 1024px)');

    // Desktop: keep the filter drawer open (re-applied after each AJAX swap).
    const forceDesktopOpen = () => {
      const drawer = document.querySelector('.filter-drawer');
      if (drawer && desktop.matches) drawer.open = true;
    };
    forceDesktopOpen();

    if (wireCollectionFilters._bound) return;
    wireCollectionFilters._bound = true;

    // Strip empty params so the pushed URL stays as clean as Shopify allows.
    const cleanUrl = (raw) => {
      const u = new URL(raw, window.location.origin);
      [...u.searchParams.entries()].forEach(([k, v]) => {
        if (v === '' || v == null) u.searchParams.delete(k);
      });
      return u;
    };

    const render = async (raw, push = true) => {
      const w = document.querySelector('[data-collection-section]');
      if (!w) return;
      const u = cleanUrl(raw);
      if (push) history.pushState({ collection: true }, '', u.pathname + u.search);
      w.style.transition = 'opacity .15s';
      w.style.opacity = '0.55';
      try {
        const sep = u.search ? '&' : '?';
        const html = await fetch(u.pathname + u.search + sep + 'section_id=' + sectionId).then((r) => r.text());
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-collection-section]');
        if (fresh) w.innerHTML = fresh.innerHTML;
      } catch (e) {
        window.location.href = u.href;
        return;
      }
      w.style.opacity = '';
      forceDesktopOpen();
    };

    const urlFromForm = (form) =>
      window.location.pathname + '?' + new URLSearchParams(new FormData(form)).toString();

    let debounce;
    document.addEventListener('change', (e) => {
      const form = e.target.closest('[data-filters-form], [data-sort-form]');
      if (!form) return;
      clearTimeout(debounce);
      debounce = setTimeout(() => render(urlFromForm(form)), e.target.type === 'number' ? 600 : 0);
    });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-filters-form], [data-sort-form]');
      if (!form) return;
      e.preventDefault();
      render(urlFromForm(form));
    });

    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-collection-section] a[href]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (href.indexOf('/products/') !== -1) return; // product cards navigate normally
      if (link.href.indexOf(window.location.pathname) === -1) return; // only same-collection links
      e.preventDefault();
      render(link.href);
    });

    window.addEventListener('popstate', () => render(window.location.href, false));
    desktop.addEventListener('change', forceDesktopOpen);
  };

  // ───────────────────────────────────────────────────────────────────
  // Click-away to close any modal <dialog> (cart, wishlist, search, mobile nav).
  // A click whose target IS the dialog element is a click on its ::backdrop —
  // i.e. outside the panel content — so close it (CSS animates the slide-out).

  const wireDialogClickAway = () => {
    document.addEventListener('mousedown', (e) => {
      const dialog = e.target;
      if (dialog instanceof HTMLDialogElement && dialog.open && e.target === dialog) {
        dialog.close();
      }
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Smart sticky header — transparent over the hero at the top; once scrolled
  // it pins as a solid frosted bar, hiding on scroll-down and showing on
  // scroll-up. (CSS handles the transitions and the .is-stuck/.is-hidden look.)

  const wireStickyHeader = () => {
    const header = document.querySelector('header[role="banner"]');
    if (!header) return;
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      if (y > 200) {
        header.classList.add('is-stuck');
        if (y > lastY + 4) header.classList.add('is-hidden');
        else if (y < lastY - 4) header.classList.remove('is-hidden');
      } else {
        header.classList.remove('is-stuck', 'is-hidden');
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  };

  // ───────────────────────────────────────────────────────────────────
  // Scroll reveal — sections fade/rise in as they enter the viewport.

  const wireScrollReveal = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    const sections = Array.from(document.querySelectorAll('main > .shopify-section'));
    // Skip the first section (hero / above-the-fold) so it shows immediately.
    const targets = sections.slice(1);
    if (!targets.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const sec = e.target;
        // Stagger any grid items inside (cards cascade); otherwise reveal the block.
        const items = sec.querySelectorAll('.reveal-item');
        if (items.length) items.forEach((it) => it.classList.add('is-in'));
        sec.classList.add('is-in');
        io.unobserve(sec);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    targets.forEach((sec) => {
      const grid = sec.querySelector('ul.grid, [data-stagger]');
      if (grid && grid.children.length >= 2) {
        // Stagger the repeated cards rather than fading the whole block.
        Array.from(grid.children).forEach((it, i) => {
          it.classList.add('reveal-item');
          it.style.transitionDelay = (i % 8) * 70 + 'ms';
        });
      } else {
        sec.classList.add('reveal');
      }
      io.observe(sec);
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Scroll parallax — decorative [data-parallax] elements drift slightly
  // against the scroll for depth. rAF-throttled, and only elements currently
  // near the viewport are updated. Applied only to accents WITHOUT their own
  // transform so we never clobber a rotate/translate.

  const wireParallax = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    const els = Array.from(document.querySelectorAll('[data-parallax]'));
    if (!els.length) return;

    const visible = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) visible.add(e.target);
          else visible.delete(e.target);
        });
      },
      { rootMargin: '120px 0px 120px 0px' }
    );
    els.forEach((el) => {
      el.style.willChange = 'transform';
      io.observe(el);
    });

    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      visible.forEach((el) => {
        const speed = parseFloat(el.dataset.parallaxSpeed) || 0.1;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        // Distance of the element's centre from the viewport centre; the accent
        // drifts a fraction of that, so it lags gently behind the scroll.
        const offset = (center - vh / 2) * speed;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
    };
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  };

  // ───────────────────────────────────────────────────────────────────
  // Boot

  const init = () => {
    syncWishlistUI();
    wireSearch();
    wireHeaderSearch();
    wireQuickAdd();
    wireHeroCarousel();
    wireBlogCarousel();
    wireTestimonialsCarousel();
    wireLastViewed();
    wireDialogClickAway();
    wireCollectionFilters();
    wireStickyHeader();
    wireScrollReveal();
    wireParallax();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
